import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  Brain, 
  Radio, 
  Wifi, 
  Car, 
  Zap, 
  TrendingUp,
  Globe,
  Server,
  Smartphone,
  Factory,
  AlertTriangle,
  Eye,
  Shield,
  Gauge,
  Layers,
  Network,
  Sparkles,
  Database
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const MarketsSection = () => {
  const [activeMarket, setActiveMarket] = useState('ai-llm');

  const markets = [
    {
      id: 'ai-llm',
      title: 'AI & Large Language Models',
      icon: Brain,
      marketSize: '$150B+',
      growth: '35% CAGR',
      color: 'from-blue-500 to-cyan-600',
      description: 'Current LLMs are powerful pattern learners but remain far less efficient and flexible than the human brain. The brain\'s vastly greater connectivity, low-power hardware, and rich learning mechanisms highlight inefficiencies in today\'s AI. Addressing these gaps is a major research frontier. MSML powered IDDO solutions targets such brain-inspired innovations (e.g. neuromorphic computing, sparse spiking networks, hybrid models), aiming to help alleviate LLMs\' energy, generalization, and adaptability problems, bringing AI closer to human-like cognition. Research-validated MSML-LLMC mathematical framework enables deployment of advanced AI models across any infrastructure. Whitepaper available detailing comprehensive use case analysis and benefits.',
      challenges: {
        title: 'Critical Industry Inefficiencies',
        overview: 'Current LLMs face fundamental inefficiencies compared to human brain architecture, creating massive computational and energy demands that limit practical deployment.',
        details: [
          {
            problem: 'Scale and Structural Inefficiency',
            description: 'While human brain operates with 86 billion neurons and ~600-1,000 trillion synaptic connections using only 20W power, LLMs require 175B-1.8T parameters consuming megawatt-hours for comparable tasks.',
            impact: 'Energy consumption 10^4-10^5 times higher than biological neural processing'
          },
          {
            problem: 'Static Learning Architecture', 
            description: 'LLMs have fixed weights post-training, unlike brain\'s continuous neuroplasticity. They cannot adapt in real-time or learn from single examples like humans.',
            impact: 'Expensive retraining required for updates; poor few-shot learning capabilities'
          },
          {
            problem: 'Computational Resource Demands',
            description: 'Training single large models can emit carbon equivalent to several cars\' lifetimes. Inference requires distributed clusters unavailable for edge deployment.',
            impact: 'Unsustainable scalability; limited accessibility for resource-constrained applications'
          }
        ]
      },
      solution: {
        title: 'MSML-LLMC Framework Solutions',
        overview: 'MSML powered IDDO directly addresses LLM inefficiencies by implementing brain-like hierarchical data organization and sparse processing principles.',
        approaches: [
          {
            method: 'Hierarchical Multi-Level Compression',
            description: 'MSML represents data at multiple abstraction levels (raw, spectral, semantic), mimicking brain\'s progressive concept formation from sensory inputs.',
            result: 'Up to 300x model size reduction with 0.5-1% accuracy loss'
          },
          {
            method: 'Perceptual Information Pruning',
            description: 'Like brain\'s synaptic pruning, MSML removes low-value data elements while preserving semantic meaning through threshold-based information criteria.',
            result: 'GPT-scale models compressed from 300GB to ~1GB while maintaining performance'
          },
          {
            method: 'Associative Memory Architecture',
            description: 'Stores and retrieves data through semantic connections rather than raw bit patterns, enabling brain-like contextual processing.',
            result: 'Edge device deployment capability; 30-60% lower carbon footprint'
          }
        ]
      },
      keyBenefits: [
        'Up to 300x model size reduction',
        'Lower computational demands',
        'Enhanced energy efficiency',
        'Cross-modal AI applications',
        'Faster training and inference cycles'
      ],
      applications: [
        'Edge AI deployment',
        'Mobile AI applications',
        'Cloud cost optimization',
        'Real-time language processing',
        'Multimodal AI systems'
      ]
    },
    {
      id: 'media',
      title: 'Media & Content Delivery',
      icon: Radio,
      marketSize: '$185B+',
      growth: '21% CAGR',
      color: 'from-purple-500 to-pink-600',
      description: 'Transform media delivery with unprecedented fidelity/data volume ratios through scientifically validated D1AUDIO technology. Demonstrable products provide solid validation ground for MSML principles.',
      challenges: {
        title: 'Media Industry Pressure Points',
        overview: 'Exponential growth in media consumption demands efficient delivery while maintaining quality, but traditional compression creates fidelity trade-offs and bandwidth bottlenecks.',
        details: [
          {
            problem: 'Quality vs File Size Dilemma',
            description: 'Traditional audio/video compression forces choice between file size and quality. Lossless formats require massive bandwidth; lossy compression sacrifices fidelity.',
            impact: 'Streaming platforms face 40-60% bandwidth costs; creators struggle with quality degradation'
          },
          {
            problem: 'Metadata and Interactive Content Limitations',
            description: 'Current codecs handle basic media but lack sophisticated metadata integration, interactive elements, or real-time synchronization capabilities.',
            impact: 'Limited immersive experiences; poor creator monetization; fragmented distribution workflows'
          },
          {
            problem: 'Infrastructure Scaling Costs',
            description: 'Growing 4K/8K content and global streaming demand exponentially increases CDN costs, storage requirements, and network infrastructure needs.',
            impact: 'Unsustainable economics for content providers; regional access inequality'
          }
        ]
      },
      solution: {
        title: 'D1AUDIO MSML Integration',
        overview: 'MSML-powered media technology achieves perceptually transparent compression while embedding rich metadata and enabling advanced distribution workflows.',
        approaches: [
          {
            method: 'Fidelity-Preserving Data Density Optimization',
            description: 'D1AUDIO codec achieves 21x data volume reduction while maintaining perceptual transparency through MSML\'s multi-structural analysis of audio signals.',
            result: '122MB original files compressed to 5.8MB with 99.8% quality retention'
          },
          {
            method: 'Programmable Media Container Technology',
            description: 'MSML enables embedding of STEMS, live lyrics, chords, and interactive elements within compressed audio streams without quality loss.',
            result: 'Enhanced creator tools; new monetization models; immersive fan experiences'
          },
          {
            method: 'Intelligent Distribution Networks',
            description: 'cDM Cloud leverages MSML\'s associative linking for content-aware caching and delivery optimization based on semantic understanding.',
            result: '75% bandwidth reduction; improved global accessibility; lower infrastructure costs'
          }
        ]
      },
      keyBenefits: [
        '21x data volume reduction',
        'Perceptually transparent quality',
        'Advanced metadata integration',
        'Cross-platform compatibility',
        'Real-time streaming optimization'
      ],
      applications: [
        'Music streaming platforms',
        'Podcast distribution',
        'Live audio broadcasting',
        'Interactive media experiences',
        'Content creator tools'
      ]
    },
    {
      id: 'telecom',
      title: 'Telecom & Edge Infrastructure',
      icon: Wifi,
      marketSize: '$91B+',
      growth: '38% CAGR',
      color: 'from-green-500 to-emerald-600',
      description: 'Leverage patented MSML principals to cut bandwidth waste and stabilize performance in 5G, satellite, and edge environments. A research whitepaper outlines the use-case analysis and results.',
      challenges: {
        title: 'Telecom Infrastructure Bottlenecks',
        overview: 'Explosive data growth from IoT, 5G, and edge computing creates network congestion, latency issues, and unsustainable infrastructure costs.',
        details: [
          {
            problem: 'Bandwidth Scarcity and Congestion',
            description: 'Mobile data traffic grows 25-35% annually while spectrum availability remains limited. 5G promises higher speeds but requires dense infrastructure investment.',
            impact: 'Network congestion during peak hours; expensive spectrum licensing; limited rural coverage'
          },
          {
            problem: 'Edge Computing Latency Requirements',
            description: 'Autonomous vehicles, AR/VR, and real-time applications demand <10ms latencies, but traditional cloud architectures introduce unacceptable delays.',
            impact: 'Safety-critical applications compromised; poor user experience; limited edge AI deployment'
          },
          {
            problem: 'Infrastructure Energy and Cooling Costs',
            description: 'Data centers consume 1% of global electricity; telecom networks require massive cooling systems. Edge deployments multiply these costs across thousands of locations.',
            impact: 'Unsustainable operational costs; environmental impact; limited edge scaling'
          }
        ]
      },
      solution: {
        title: 'MSML Network Optimization',
        overview: 'MSML transforms network data flows by implementing intelligent compression and edge processing that dramatically reduces bandwidth requirements while improving performance.',
        approaches: [
          {
            method: 'Intelligent Data Flow Compression',
            description: 'MSML analyzes network traffic patterns and compresses data streams based on content importance and user context, not just raw bandwidth reduction.',
            result: 'Up to 75% bandwidth savings while maintaining Quality of Service requirements'
          },
          {
            method: 'Edge-Native Processing Architecture',
            description: 'Compressed AI models enable sophisticated processing at network edges, reducing round-trip delays and central processing demands.',
            result: '<12ms latency for critical applications; 60% reduction in core network traffic'
          },
          {
            method: 'Adaptive Network Resource Management',
            description: 'MSML\'s multi-level optimization dynamically allocates network resources based on real-time demand patterns and content priority.',
            result: '40% improvement in network efficiency; predictive scaling capabilities'
          }
        ]
      },
      keyBenefits: [
        'Up to 75% bandwidth savings',
        'Improved network stability',
        'Reduced infrastructure costs',
        'Enhanced edge computing',
        'Lower latency performance'
      ],
      applications: [
        '5G network optimization',
        'Satellite communications',
        'IoT device connectivity',
        'Edge computing platforms',
        'Network function virtualization'
      ]
    },
    {
      id: 'automotive',
      title: 'Autonomous Vehicles & Robotics',
      icon: Car,
      marketSize: '$76B+',
      growth: '40% CAGR',
      color: 'from-orange-500 to-red-600',
      description: 'MSML-powered Intelligent Data Density Optimization enabling next-generation autonomous systems with enhanced perception, decision-making, and operational efficiency.',
      // Extended constraints with IDDO solutions
      extendedConstraints: [
        {
          title: 'Data Overload',
          description: 'Autonomous vehicles generate massive volumes of sensor data that overwhelm traditional processing systems.',
          impact: 'Storage and transmission bottlenecks',
          solution: 'MSML compression enables potential reduction approaching 40% in sensor data loads at the edge'
        },
        {
          title: 'Computational Demands',
          description: 'Complex AI models require substantial computing power, limiting deployment on edge devices.',
          impact: 'High processing costs and latency',
          solution: 'MSML-LLMC framework offers possibilities for up to 3x model size reduction with accuracy maintained near 99.5%'
        },
        {
          title: 'Bandwidth Bottlenecks',
          description: 'Real-time transmission of high-resolution sensor feeds challenges existing network infrastructure.',
          impact: 'Delays in critical data delivery',
          solution: 'Intelligent data density optimization potentially achieves bandwidth reduction approaching 40% for cloud streaming'
        },
        {
          title: 'Power Constraints',
          description: 'Energy-intensive AI operations drain battery resources and generate excessive heat.',
          impact: 'Limited operational range',
          solution: 'Optimized models demonstrate potential for significant power efficiency improvements and reduced thermal load'
        },
        {
          title: 'Edge Compute Limitations',
          description: 'Onboard hardware resources are finite, restricting the complexity of deployable AI models.',
          impact: 'Trade-off between capability and feasibility',
          solution: 'Compressed models enable deployment of advanced AI on automotive-grade chips with enhanced performance possibilities'
        },
        {
          title: 'Scalability Challenges',
          description: 'Deploying autonomous systems at scale requires cost-effective solutions without compromising safety.',
          impact: 'High infrastructure and operational costs',
          solution: 'IDDO solutions offer potential for substantial cost reductions in storage, transmission, and compute resources'
        }
      ],
      // Autonomous Intelligence Categories
      intelligenceCategories: [
        {
          category: 'Enhanced Perception',
          capabilities: [
            {
              name: 'Acoustic Intelligence',
              description: 'Advanced audio processing prioritizing critical sensor data with voice-activated controls and real-time sound diagnostics',
              benefit: 'Augmented situational awareness through auditory scene analysis'
            },
            {
              name: 'AudioDNA Pattern Recognition',
              description: 'Robust AI for identifying and classifying environmental sounds including sirens, horns, and emergency signals',
              benefit: 'Enhanced safety through multi-modal perception fusion'
            },
            {
              name: 'Intelligent Sensor Fusion',
              description: 'MSML-driven filtering of redundant data while highlighting critical features for decision-making',
              benefit: 'Focused compute on mission-critical information'
            }
          ]
        },
        {
          category: 'Advanced Decision-Making',
          capabilities: [
            {
              name: 'Edge AI Deployment',
              description: 'MSML-LLMC framework enabling sophisticated AI models on resource-constrained edge hardware',
              benefit: 'Real-time autonomous decisions without cloud dependency'
            },
            {
              name: 'Adaptive Intelligence',
              description: 'AI-driven software combining multi-sensor data streams for comprehensive environmental understanding',
              benefit: 'Context-aware responses to dynamic driving scenarios'
            },
            {
              name: 'Predictive Processing',
              description: 'Optimized models achieving faster inference with possibilities for reduced latency approaching critical thresholds',
              benefit: 'Proactive rather than reactive autonomous behavior'
            }
          ]
        },
        {
          category: 'Safety & Security',
          capabilities: [
            {
              name: 'Data Integrity',
              description: 'LDE digital fingerprints creating immutable chain-of-custody for forensically robust data records',
              benefit: 'Tamper-proof audit trails for regulatory compliance'
            },
            {
              name: 'Secure Communication',
              description: 'Encryption and DRM protecting OTA updates and vehicle-to-cloud data transmission',
              benefit: 'Protection against cybersecurity threats and IP theft'
            },
            {
              name: 'Synchronized Operations',
              description: 'Real-time synchronization ensuring consistent state across distributed AV systems',
              benefit: 'Coordinated fleet operations with enhanced reliability'
            }
          ]
        },
        {
          category: 'Operational Excellence',
          capabilities: [
            {
              name: 'Resource Optimization',
              description: 'Potential for substantial reductions in data storage, transmission bandwidth, and computational overhead',
              benefit: 'Lower operational costs enabling scalable deployment'
            },
            {
              name: 'Rapid OTA Updates',
              description: 'Compressed model binaries reducing update times and vehicle downtime for software deployments',
              benefit: 'Continuous improvement with minimal service interruption'
            },
            {
              name: 'Enhanced Uptime',
              description: 'Efficient thermal management and reduced power consumption contributing to increased operational availability',
              benefit: 'Maximized revenue-generating hours per vehicle'
            }
          ]
        }
      ],
      // Technology Synergies
      technologySynergies: [
        {
          title: 'Edge Data Compression',
          description: 'Hierarchical MSML framework demonstrates potential for sensor data reduction approaching 30-40% while preserving perceptual fidelity',
          applications: ['LIDAR point clouds', 'Multi-camera feeds', 'Radar telemetry']
        },
        {
          title: 'AI Model Optimization',
          description: 'MSML-LLMC compression offers possibilities for up to 3x reduction in neural network size with accuracy potentially maintained above 99.5%',
          applications: ['Perception models', 'Planning algorithms', 'Control systems']
        },
        {
          title: 'Low-Latency Connectivity',
          description: 'cDM Cloud infrastructure targeting sub-10ms latency for critical teleoperation and V2X communication scenarios',
          applications: ['Remote assistance', 'Fleet coordination', 'Infrastructure integration']
        },
        {
          title: 'In-Cabin Experience',
          description: 'D1 Audio codec and programmable containers enabling high-fidelity multimedia with synchronized content delivery',
          applications: ['Passenger entertainment', 'Voice interaction', 'Ambient environment']
        }
      ],
      challenges: {
        title: 'Autonomous Systems Constraints', 
        overview: 'Safety-critical autonomous systems require real-time AI processing but face severe power, compute, and latency constraints that limit deployment of advanced models.',
        details: [
          {
            problem: 'Real-Time Processing Under Power Constraints',
            description: 'Autonomous vehicles need millisecond decision-making using onboard computers with limited power budgets. Current AI models are too large and energy-hungry for reliable operation.',
            impact: 'Safety risks from delayed responses; limited operational range; expensive specialized hardware'
          },
          {
            problem: 'Multi-Modal Sensor Integration',
            description: 'Autonomous systems process LiDAR, cameras, radar, and GPS simultaneously. Traditional approaches create data bottlenecks and processing delays.',
            impact: 'Reduced situational awareness; sensor fusion complexity; degraded performance in challenging conditions'
          },
          {
            problem: 'Edge Reliability and Connectivity',
            description: 'Autonomous systems must function without constant cloud connectivity, but onboard AI models are often too limited for complex scenarios.',
            impact: 'Operational limitations in remote areas; safety concerns during connectivity loss; high cloud computing costs'
          }
        ]
      },
      solution: {
        title: 'MSML Autonomous Intelligence',
        overview: 'MSML enables deployment of sophisticated AI models on resource-constrained autonomous systems through extreme compression and efficient multi-modal processing. It scores chunks of data by how much they help your tasks and how little redundancy they carry, then keeps the high-value data and downshifts or drops the rest. In AV terms: It preserve the bits that move safety-critical perception and control, and compress the "wallpaper".',
        approaches: [
          {
            method: 'Ultra-Compressed Onboard AI Models',
            description: 'MSML demonstrates potential for compressing computer vision and decision-making models while preserving critical safety and navigation capabilities.',
            result: 'Full autonomous AI stack possibilities on embedded processors with potential for significant power reduction'
          },
          {
            method: 'Hierarchical Sensor Data Processing',
            description: 'Multi-structural analysis processes sensor data at different abstraction levels through defined stages: raw sensor input → object detection → classification → tracking. System architecture separates perception pipeline from control loops for measurable performance metrics.',
            result: '<5ms for perception-only latency (sensor-to-track), sub-10ms for closed-loop control, and guaranteed worst-case execution time (WCET) of <2ms for critical object detection in safety-critical paths'
          },
          {
            method: 'Adaptive Edge Intelligence',
            description: 'MSML enables dynamic model adaptation based on operational context, optimizing performance for current conditions while maintaining safety margins.',
            result: 'Context-aware performance optimization possibilities with reduced false positives and enhanced reliability'
          }
        ]
      },
      keyBenefits: [
        'Real-time processing capabilities',
        'Potential for significant power consumption reduction',
        'Enhanced safety protocols',
        'Multi-sensor data fusion',
        'Predictive analytics integration'
      ],
      applications: [
        'Autonomous vehicle systems',
        'Industrial robotics',
        'Drone navigation',
        'Smart manufacturing',
        'Embedded AI systems'
      ]
    },
    {
      id: 'energy',
      title: 'Energy & Smart Grid',
      icon: Zap,
      marketSize: '$84B+',
      growth: '28% CAGR',
      color: 'from-yellow-500 to-orange-600',
      description: 'Optimize energy distribution and smart grid operations through intelligent data density optimization. MSML technology enables real-time grid management and predictive maintenance.',
      challenges: {
        title: 'Smart Grid Complexity Challenges',
        overview: 'Modern electrical grids generate massive data volumes from IoT sensors, smart meters, and distributed energy sources, creating analysis bottlenecks that limit optimization and reliability.',
        details: [
          {
            problem: 'Data Volume and Processing Overload',
            description: 'Smart grids generate terabytes of sensor data daily from millions of devices. Traditional systems cannot process this volume in real-time for optimal decision-making.',
            impact: 'Delayed grid optimization; reactive instead of predictive maintenance; inefficient energy distribution'
          },
          {
            problem: 'Renewable Energy Integration Complexity',
            description: 'Solar and wind sources create unpredictable generation patterns requiring sophisticated forecasting and real-time balancing algorithms.',
            impact: 'Grid instability; energy waste; difficulty scaling renewable adoption; expensive backup systems'
          },
          {
            problem: 'Legacy Infrastructure Limitations',
            description: 'Aging grid infrastructure lacks computational power for advanced analytics while requiring expensive upgrades for smart grid capabilities.',
            impact: 'Limited modernization options; high upgrade costs; poor integration with new technologies'
          }
        ]
      },
      solution: {
        title: 'MSML Smart Grid Intelligence',
        overview: 'MSML transforms energy grid operations through intelligent data compression and hierarchical analysis that enables real-time optimization across massive distributed systems.',
        approaches: [
          {
            method: 'Intelligent Grid Data Compression',
            description: 'MSML compresses sensor data streams by identifying critical patterns while preserving essential grid monitoring and control information.',
            result: '85% data volume reduction enabling real-time grid-wide analysis and optimization'
          },
          {
            method: 'Predictive Energy Flow Modeling',
            description: 'Multi-level analysis processes generation, consumption, and weather data to predict energy demands and optimize distribution before imbalances occur.',
            result: '40% improvement in grid stability; 25% reduction in energy waste; enhanced renewable integration'
          },
          {
            method: 'Edge-Deployed Grid Intelligence',
            description: 'Compressed AI models deployed at substations and generation facilities enable local optimization while maintaining grid-wide coordination.',
            result: 'Distributed intelligence capability; improved response times; reduced central processing demands'
          }
        ]
      },
      keyBenefits: [
        'Grid optimization efficiency',
        'Predictive maintenance alerts',
        'Demand response automation',
        'Renewable energy integration',
        'Real-time load balancing'
      ],
      applications: [
        'Smart grid management',
        'Renewable energy optimization',
        'Power plant monitoring',
        'Energy trading platforms',
        'Utility infrastructure'
      ]
    }
  ];

  const totalMarketSize = markets.reduce((total, market) => {
    const size = parseInt(market.marketSize.replace(/[^\d]/g, ''));
    return total + size;
  }, 0);

  return (
    <section id="markets" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-px h-16 bg-gradient-to-b from-cyan-400/50 to-transparent"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 0.7, 0],
              y: [0, -40]
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Target <span className="text-cyan-400">Markets</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            MSML technology addresses critical challenges across multiple high-growth industries
          </p>
          
          {/* Total Addressable Market */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center bg-slate-800/50 rounded-full px-8 py-4 border border-cyan-400/20"
          >
            <TrendingUp className="text-cyan-400 mr-3" size={24} />
            <div className="text-left">
              <div className="text-2xl font-bold text-cyan-400">${totalMarketSize}B+</div>
              <div className="text-sm text-gray-400">Total Addressable Market</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Market Grid Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16"
        >
          {markets.map((market, index) => {
            const IconComponent = market.icon;
            return (
              <motion.div
                key={market.id}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => setActiveMarket(market.id)}
                className="cursor-pointer"
              >
                <Card className={`h-full bg-slate-800/50 border-slate-600 hover:border-cyan-400/40 transition-all duration-300 ${
                  activeMarket === market.id ? 'border-cyan-400/60 bg-slate-700/50' : ''
                }`}>
                  <CardContent className="p-6 text-center">
                    <div className={`w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-r ${market.color} flex items-center justify-center`}>
                      <IconComponent className="text-white" size={20} />
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-2">{market.title}</h3>
                    <div className="text-2xl font-bold text-cyan-400 mb-1">{market.marketSize}</div>
                    <Badge variant="outline" className="text-xs border-gray-500 text-gray-400">
                      {market.growth}
                    </Badge>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Detailed Market Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {markets.map((market) => {
            if (activeMarket !== market.id) return null;
            
            const IconComponent = market.icon;
            return (
              <motion.div
                key={market.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="bg-slate-800/50 border-cyan-400/20 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${market.color} flex items-center justify-center`}>
                        <IconComponent className="text-white" size={28} />
                      </div>
                      <div>
                        <CardTitle className="text-2xl text-white">{market.title}</CardTitle>
                        <div className="flex items-center space-x-4 mt-2">
                          <Badge className={`bg-gradient-to-r ${market.color} text-white`}>
                            Market Size: {market.marketSize}
                          </Badge>
                          <Badge variant="outline" className="border-green-400 text-green-400">
                            Growth: {market.growth}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <CardDescription className="text-gray-300 text-lg leading-relaxed">
                      {market.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Industry Challenges Section */}
                    {market.challenges && (
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-red-400 mb-4">{market.challenges.title}</h4>
                        <p className="text-gray-300 mb-6 leading-relaxed">{market.challenges.overview}</p>
                        <div className="space-y-4">
                          {market.challenges.details.map((challenge, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                              className="bg-red-900/20 border border-red-400/20 rounded-lg p-4"
                            >
                              <h5 className="text-red-300 font-semibold mb-2">{challenge.problem}</h5>
                              <p className="text-gray-300 text-sm mb-2">{challenge.description}</p>
                              <p className="text-red-200 text-sm font-medium">Impact: {challenge.impact}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* MSML Solutions Section */}
                    {market.solution && (
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-green-400 mb-4">{market.solution.title}</h4>
                        <p className="text-gray-300 mb-6 leading-relaxed">{market.solution.overview}</p>
                        <div className="space-y-4">
                          {market.solution.approaches.map((approach, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                              className="bg-green-900/20 border border-green-400/20 rounded-lg p-4"
                            >
                              <h5 className="text-green-300 font-semibold mb-2">{approach.method}</h5>
                              <p className="text-gray-300 text-sm mb-2">{approach.description}</p>
                              <p className="text-green-200 text-sm font-medium">Expected Result: {approach.result}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Extended Constraints (Automotive Only) */}
                    {market.extendedConstraints && market.id === 'automotive' && (
                      <div className="mb-8">
                        <h4 className="text-2xl font-semibold text-orange-400 mb-6 text-center">Detailed Autonomous Systems Constraints</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {market.extendedConstraints.map((constraint, i) => {
                            const iconMap = {
                              'Data Overload': Database,
                              'Computational Demands': Brain,
                              'Bandwidth Bottlenecks': Wifi,
                              'Power Constraints': Zap,
                              'Edge Compute Limitations': Gauge,
                              'Scalability Challenges': TrendingUp
                            };
                            const IconComponent = iconMap[constraint.title] || AlertTriangle;
                            
                            return (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                                className="bg-slate-900/50 border border-orange-400/30 rounded-lg p-4 hover:border-orange-400/60 transition-all"
                              >
                                <div className="flex items-center gap-2 mb-3">
                                  <IconComponent className="text-orange-400" size={24} />
                                  <h5 className="text-white font-semibold text-sm">{constraint.title}</h5>
                                </div>
                                <p className="text-gray-300 text-xs mb-2">{constraint.description}</p>
                                <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
                                  <div>
                                    <p className="text-[10px] text-gray-500">Impact:</p>
                                    <p className="text-xs text-red-400">{constraint.impact}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-gray-500">IDDO Solution:</p>
                                    <p className="text-xs text-green-400">{constraint.solution}</p>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Autonomous Intelligence Categories (Automotive Only) */}
                    {market.intelligenceCategories && market.id === 'automotive' && (
                      <div className="mb-8">
                        <h4 className="text-2xl font-semibold text-purple-400 mb-6 text-center">MSML Autonomous Intelligence</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {market.intelligenceCategories.map((category, catIndex) => {
                            const iconMap = {
                              'Enhanced Perception': Eye,
                              'Advanced Decision-Making': Brain,
                              'Safety & Security': Shield,
                              'Operational Excellence': Gauge
                            };
                            const IconComponent = iconMap[category.category] || Brain;
                            const gradients = [
                              'from-cyan-500 to-blue-600',
                              'from-purple-500 to-pink-600',
                              'from-green-500 to-emerald-600',
                              'from-orange-500 to-red-600'
                            ];
                            
                            return (
                              <motion.div
                                key={catIndex}
                                initial={{ opacity: 0, x: catIndex % 2 === 0 ? -50 : 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: catIndex * 0.2, duration: 0.6 }}
                              >
                                <Card className="bg-slate-900/50 border-purple-400/20 backdrop-blur-sm h-full">
                                  <CardHeader>
                                    <div className="flex items-center gap-3 mb-2">
                                      <div className={`p-3 rounded-lg bg-gradient-to-br ${gradients[catIndex]}`}>
                                        <IconComponent className="text-white" size={24} />
                                      </div>
                                      <CardTitle className="text-lg text-white">{category.category}</CardTitle>
                                    </div>
                                  </CardHeader>
                                  <CardContent>
                                    <Accordion type="single" collapsible className="w-full">
                                      {category.capabilities.map((capability, capIndex) => (
                                        <AccordionItem key={capIndex} value={`item-${catIndex}-${capIndex}`} className="border-gray-700">
                                          <AccordionTrigger className="text-white hover:text-purple-400 text-left text-sm">
                                            {capability.name}
                                          </AccordionTrigger>
                                          <AccordionContent className="text-gray-300 space-y-2">
                                            <p className="text-xs">{capability.description}</p>
                                            <div className="bg-purple-500/10 border border-purple-400/30 rounded-lg p-2 mt-2">
                                              <p className="text-[10px] text-purple-400 font-semibold mb-1">Benefit:</p>
                                              <p className="text-xs text-purple-300">{capability.benefit}</p>
                                            </div>
                                          </AccordionContent>
                                        </AccordionItem>
                                      ))}
                                    </Accordion>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Expected Outcome (Automotive Only) */}
                    {market.technologySynergies && market.id === 'automotive' && (
                      <div className="mb-8">
                        <h4 className="text-2xl font-semibold text-cyan-400 mb-6 text-center">Expected Outcome</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {market.technologySynergies.map((synergy, i) => {
                            const iconMap = {
                              'Edge Data Compression': Layers,
                              'AI Model Optimization': Brain,
                              'Low-Latency Connectivity': Network,
                              'In-Cabin Experience': Sparkles
                            };
                            const IconComponent = iconMap[synergy.title] || Brain;
                            
                            return (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.15, duration: 0.5 }}
                              >
                                <Card className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border-cyan-400/20 backdrop-blur-sm h-full hover:border-cyan-400/50 transition-all">
                                  <CardHeader>
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-cyan-500/20 rounded-lg">
                                        <IconComponent className="text-cyan-400" size={20} />
                                      </div>
                                      <CardTitle className="text-base text-white">{synergy.title}</CardTitle>
                                    </div>
                                  </CardHeader>
                                  <CardContent className="space-y-3">
                                    <p className="text-gray-300 text-sm leading-relaxed">{synergy.description}</p>
                                    
                                    <div>
                                      <p className="text-xs text-gray-500 mb-2">Applications:</p>
                                      <div className="flex flex-wrap gap-2">
                                        {synergy.applications.map((app, appIndex) => (
                                          <Badge key={appIndex} variant="outline" className="border-cyan-400/30 text-cyan-400 text-xs">
                                            {app}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Key Benefits */}
                      <div>
                        <h4 className="text-lg font-semibold text-cyan-400 mb-4">MSML Technology Benefits</h4>
                        <ul className="space-y-3">
                          {market.keyBenefits.map((benefit, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                              className="flex items-start text-gray-300"
                            >
                              <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {benefit}
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      {/* Applications */}
                      <div>
                        <h4 className="text-lg font-semibold text-cyan-400 mb-4">Target Applications</h4>
                        <ul className="space-y-3">
                          {market.applications.map((application, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                              className="flex items-start text-gray-300"
                            >
                              <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {application}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-600">
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                          onClick={() => window.location.href = 'mailto:markets@generalharmonics.com'}
                        >
                          Request Market Analysis
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900"
                          onClick={() => toast({
                            title: "Document Access Restricted",
                            description: "Requested document requires signed NDA, please contact the company through the correspondent contact form.",
                            duration: 5000
                          })}
                        >
                          Download Whitepaper
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
};

export default MarketsSection;